import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const CONFIGURABLE_ROLES = [
  { code: 'citizen',   label: '👤 Citizen',    desc: 'Normal citizen users (OTP login)' },
  { code: 'operator1', label: '🖥️ Operator 1',  desc: 'Counter operator — full NOC access' },
  { code: 'operator2', label: '🖥️ Operator 2',  desc: 'Counter operator — full NOC access' },
];

export default function NocAccessConfig() {
  const [nocTypes, setNocTypes]   = useState([]);
  const [access, setAccess]       = useState({});  // { role_code: Set(noc_type_ids) }
  const [saving, setSaving]       = useState({});
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      try {
        const nocRes = await api.get('/masters/noc-types');
        const allNoc = nocRes.noc_types || [];
        setNocTypes(allNoc);

        // Load current access for each role
        const accessMap = {};
        for (const role of CONFIGURABLE_ROLES) {
          try {
            const r = await api.get(`/role-noc-access/${role.code}`);
            accessMap[role.code] = new Set(r.noc_type_ids || []);
          } catch {
            accessMap[role.code] = new Set();
          }
        }
        setAccess(accessMap);
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  const toggleNoc = (roleCode, nocId) => {
    setAccess(prev => {
      const current = new Set(prev[roleCode] || []);
      if (current.has(nocId)) current.delete(nocId);
      else current.add(nocId);
      return { ...prev, [roleCode]: current };
    });
  };

  const selectAll = (roleCode) => {
    setAccess(prev => ({ ...prev, [roleCode]: new Set(nocTypes.map(n => n.id)) }));
  };

  const clearAll = (roleCode) => {
    setAccess(prev => ({ ...prev, [roleCode]: new Set() }));
  };

  const saveRole = async (roleCode) => {
    setSaving(s => ({ ...s, [roleCode]: true }));
    try {
      const ids = Array.from(access[roleCode] || []);
      await api.post('/role-noc-access', { role_code: roleCode, noc_type_ids: ids });
      toast.success(`Access saved for ${roleCode}!`);
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(s => ({ ...s, [roleCode]: false }));
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">🔑 NOC Access Configuration</h1>
        <p className="text-sm text-gray-500 mt-1">
          Control which NOC types each role can see and apply for
        </p>
      </div>

      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
        <p className="font-semibold mb-1">ℹ️ How it works:</p>
        <p className="text-xs text-blue-700">
          • <strong>Citizen</strong> — by default sees only Dhara 80 &amp; Land Use. Add more NOCs here.<br/>
          • <strong>Operator 1 &amp; 2</strong> — select ALL to give them access to all NOC types.<br/>
          • <strong>Admin/Developer/Dept Officers</strong> — always see all NOCs (not configurable here).
        </p>
      </div>

      <div className="space-y-6">
        {CONFIGURABLE_ROLES.map(role => {
          const roleAccess = access[role.code] || new Set();
          const selectedCount = roleAccess.size;
          return (
            <div key={role.code} className="card">
              {/* Role header */}
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                  <h3 className="font-bold text-gray-800 text-lg">{role.label}</h3>
                  <p className="text-xs text-gray-500">{role.desc}</p>
                  <p className="text-xs text-blue-600 mt-1 font-medium">
                    {selectedCount === 0
                      ? '⚠️ No access configured — fallback to Dhara80 + Land Use'
                      : selectedCount === nocTypes.length
                        ? '✅ Full access — all NOC types'
                        : `✅ ${selectedCount} of ${nocTypes.length} NOC types selected`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => selectAll(role.code)}
                    className="text-xs btn-secondary px-3 py-1.5">
                    ✅ Select All
                  </button>
                  <button onClick={() => clearAll(role.code)}
                    className="text-xs bg-gray-100 text-gray-600 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-200">
                    ✕ Clear All
                  </button>
                  <button
                    onClick={() => saveRole(role.code)}
                    disabled={saving[role.code]}
                    className="btn-primary text-xs px-4 py-1.5">
                    {saving[role.code] ? '⏳ Saving...' : '💾 Save'}
                  </button>
                </div>
              </div>

              {/* NOC type checkboxes */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {nocTypes.map(noc => {
                  const checked = roleAccess.has(noc.id);
                  return (
                    <label key={noc.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all
                        ${checked
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300 bg-white'}`}>
                      <input type="checkbox" checked={checked}
                        onChange={() => toggleNoc(role.code, noc.id)}
                        className="w-4 h-4 rounded text-blue-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{noc.noc_name}</p>
                        <p className="text-xs font-mono text-gray-400">{noc.noc_code}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}